import axios from "axios";

const API_BASE_URL =
  window._env_?.VITE_API_URL ||
  import.meta.env.VITE_API_URL ||
  "http://localhost:5000/api/v1";

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { "Content-Type": "application/json" },
  timeout: 30000,
});

api.interceptors.request.use(
  (config) => {
    // Always read fresh token from localStorage
    const token = localStorage.getItem("access_token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (config.data instanceof FormData) {
      delete config.headers["Content-Type"];
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Do not intercept 401s for login or register endpoints - the component will handle the error
    if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/register')) {
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem("refresh_token");
        if (!refreshToken) {
          throw new Error("No refresh token");
        }

        console.log("🔄 Attempting token refresh...");

        const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refresh_token: refreshToken,
        });

        console.log("📦 Refresh response received:", data);

        // Handle multiple possible response structures
        const responseData = data.data || data;
        const tokens = responseData.tokens || responseData;
        
        // Try all possible token field names
        const newAccessToken = 
          tokens.accessToken || 
          tokens.access_token || 
          responseData.accessToken || 
          responseData.access_token;
          
        const newRefreshToken = 
          tokens.refreshToken || 
          tokens.refresh_token || 
          responseData.refreshToken || 
          responseData.refresh_token;

        if (!newAccessToken || !newRefreshToken) {
          console.error("❌ Invalid refresh response structure:", {
            data,
            responseData,
            tokens,
            hasAccessToken: !!newAccessToken,
            hasRefreshToken: !!newRefreshToken,
          });
          throw new Error("Invalid token refresh response");
        }

        // Update localStorage
        localStorage.setItem("access_token", newAccessToken);
        localStorage.setItem("refresh_token", newRefreshToken);

        // Update axios default headers and original request
        api.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        console.log("✅ Token refreshed successfully");

        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        console.error("❌ Token refresh failed:", refreshError);
        
        // Clear all auth data
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        localStorage.removeItem("user");
        
        // Redirect to login
        window.location.href = "/login";
        
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  },
);

export default api;